/**
 * Catalogues de traduction.
 *
 * ## La garantie qui compte
 *
 * `en` est déclaré `Record<MessageKey, string>` : **oublier une traduction devient une
 * erreur de compilation**, pas un texte français qui s'affiche au milieu d'une interface
 * anglaise. Idem pour une clé en trop. C'est la seule façon fiable de garder deux langues
 * synchronisées sans outil externe.
 *
 * ## Convention de nommage
 *
 * `<domaine>.<élément>` — `home.title`, `room.errorNoKey`. Les pluriels utilisent les
 * suffixes `.one` et `.other`, résolus par `plural()` plutôt qu'à la main aux points
 * d'appel.
 *
 * ## Pluriels
 *
 * Les langues supportées n'ont que deux formes (singulier, pluriel), ce que la règle
 * `count === 1 ? 'one' : 'other'` couvre exactement. Une langue à formes multiples
 * demanderait `Intl.PluralRules` — hors périmètre, et à traiter le jour où une telle
 * langue est ajoutée plutôt que par anticipation.
 */

export const LOCALES = ['fr', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

/** Libellés affichés dans le sélecteur de langue, dans leur propre langue. */
export const LOCALE_LABELS: Record<Locale, string> = {
  fr: 'Français',
  en: 'English',
};

export const fr = {
  // ── Coquille ──────────────────────────────────────────────────────────────
  'app.brand': 'Attrape!',
  'app.tagline': 'Aucun fichier n’est stocké sur le serveur',
  'app.titleHome': 'Partage direct de fichiers — sans serveur, sans limite',
  'app.titleRoom': 'Partage en cours…',
  'app.footerMain':
    'Transfert direct, chiffré de bout en bout. L’onglet fermé, le partage s’arrête.',
  'app.footerDetail': 'Instance auto-hébergée — la clé ne transite jamais par le serveur.',
  'app.language': 'Langue',
  'app.themeToLight': 'Passer au thème clair',
  'app.themeToDark': 'Passer au thème sombre',

  // ── Accueil ───────────────────────────────────────────────────────────────
  'home.title': 'Envoyer un fichier, sans limite de taille',
  'home.titleLine1': 'Partagez',
  'home.titleLine2': 'en direct.',
  'home.tag': 'Simple · Privé · Sans limite',
  'home.lede':
    'Les fichiers vont directement de votre appareil à celui de votre correspondant. Ils ne sont ni téléversés ni conservés : fermez l’onglet, le partage s’arrête.',
  'home.filesReady.one': '{count} fichier prêt',
  'home.filesReady.other': '{count} fichiers prêts',
  'home.andMore.one': 'et {count} autre…',
  'home.andMore.other': 'et {count} autres…',
  'home.createLink': 'Créer le lien de partage',
  'home.allowMultiple': 'Autoriser plusieurs destinataires',
  'home.allowMultipleOn':
    'Le lien restera valable : plusieurs personnes pourront l’ouvrir tant que la salle existe.',
  'home.allowMultipleOff':
    'Le lien ne fonctionnera qu’une fois. Dès que la personne l’ayant ouvert repart, il devient inutilisable — même pour elle.',
  'home.clearAll': 'Tout retirer',
  'home.explainLabel': 'Comment ça marche ?',
  'home.explain1Title': 'Déposez',
  'home.explain1Body': 'Vos fichiers restent sur votre appareil.',
  'home.explain2Title': 'Partagez le lien',
  'home.explain2Body': 'La clé voyage avec lui, jamais vers le serveur.',
  'home.explain3Title': 'Connexion directe',
  'home.explain3Body': 'Vos appareils se parlent en pair à pair.',
  'home.explain4Title': 'Transfert sécurisé',
  'home.explain4Body': 'Chiffré de bout en bout, automatiquement.',
  'home.badgeLine1': 'Sans serveur.',
  'home.badgeLine2': '100% vous.',
  'home.deviceSender': 'Votre appareil',
  'home.deviceReceiver': 'Appareil de réception',
  'home.techLabel': 'Sécurité & technologies',
  'home.tech1Value': 'AES-256',
  'home.tech1Label': 'Chiffrement de bout en bout',
  'home.tech2Value': 'SHA-256',
  'home.tech2Label': 'Intégrité vérifiée à l’arrivée',
  'home.tech3Value': 'WebRTC',
  'home.tech3Label': 'Connexion P2P directe entre navigateurs',
  'home.tech4Value': '0',
  'home.tech4Label': 'Fichier stocké sur le serveur',

  // ── Dépôt de fichiers ─────────────────────────────────────────────────────
  'drop.title': 'Choisir des fichiers à envoyer',
  'drop.hint': 'ou glissez-les ici — ou collez-les avec Ctrl/⌘ + V',

  // ── Partage ───────────────────────────────────────────────────────────────
  'share.copy': 'Copier le lien',
  'share.copied': 'Copié',
  'share.label': 'Lien de partage',
  'share.hint':
    'La clé, après le caractère #, n’est jamais envoyée au serveur. Ne publiez pas ce lien.',
  'share.qrLabel': 'Code à scanner pour ouvrir ce lien',
  'share.qrHint': 'Scannez ce code avec l’appareil photo du téléphone.',
  'share.share': 'Partager',
  'share.install': 'Installer l’application',

  // ── Demande entrante ──────────────────────────────────────────────────────
  'incoming.title.one': 'Un correspondant veut vous envoyer un fichier',
  'incoming.title.other': 'Un correspondant veut vous envoyer {count} fichiers',
  'incoming.total': 'Total : {size}. Rien ne sera écrit sur le disque avant votre accord.',
  'incoming.andMore.one': 'et {count} autre…',
  'incoming.andMore.other': 'et {count} autres…',
  'incoming.accept': 'Accepter et choisir la destination',
  'incoming.reject': 'Refuser',

  // ── Progression ───────────────────────────────────────────────────────────
  'progress.preparing': 'Préparation du transfert…',
  'progress.fileOfTotal': 'Fichier {index} sur {count}',
  'progress.overall': '{percent} % de l’ensemble',
  'progress.remaining': 'reste {duration}',
  'progress.cancel': 'Interrompre',
  'progress.sending': 'Envoi en cours',
  'progress.receiving': 'Réception en cours',

  // ── État de la connexion ──────────────────────────────────────────────────
  'status.connected': 'Serveur connecté',
  'status.connecting': 'Connexion au serveur…',
  'status.reconnecting': 'Reconnexion…',
  'status.refused': 'Connexion refusée',
  'status.disconnected': 'Déconnecté',
  'status.waiting': 'En attente',
  'status.pathUnknown': 'Établissement du chemin…',
  'status.directLocal': 'Connexion directe — réseau local',
  'status.directPeer': 'Connexion directe — pair à pair',
  'status.relayed': 'Connexion relayée par le serveur',
  'status.established': 'Connexion établie',
  'room.relaySlow':
    'La connexion directe entre les deux appareils a échoué : les données passent par le relais du serveur, plus lent.',
  'room.relaySlowBig':
    'La connexion directe entre les deux appareils a échoué : les données passent par le relais du serveur, plus lent. Pour {size}, comptez environ {eta}. Laissez les deux onglets ouverts jusque-là.',
  'status.roleSender': 'Émetteur',
  'status.roleReceiver': 'Destinataire',

  // ── Salle ─────────────────────────────────────────────────────────────────
  'room.errorNoKey':
    'Ce lien est incomplet : la clé de déchiffrement, située après le caractère #, est absente. Demandez le lien entier à votre correspondant.',
  'room.createNewShare': 'Créer un nouveau partage',
  'room.errorForgotten':
    'Vous aviez créé ce partage, mais les fichiers ont été oubliés lors du rechargement de la page. Le navigateur ne permet pas de les retrouver sans votre accord — c’est aussi ce qui garantit que rien ne fuit.',
  'room.chooseFiles': 'Choisir des fichiers',
  'room.newShare': 'Nouveau partage',
  'room.connecting': 'Connexion au serveur…',
  'room.connectingHint':
    'Établissement du lien avec le service qui met les deux appareils en relation.',
  'room.waitingPeer': 'En attente du destinataire',
  'room.waitingPeerHint':
    'Partagez ce lien. Dès qu’il est ouvert, la connexion s’établit automatiquement.',
  'room.keepInMind': 'À garder en tête',
  'room.keep1': 'Cet onglet doit rester ouvert : c’est lui qui envoie les fichiers.',
  'room.keep2': 'Le transfert démarre dès que votre correspondant accepte.',
  'room.keep3':
    'Si la connexion directe est impossible, un message vous l’indiquera clairement plutôt que de rester bloqué.',
  'room.cancelShare': 'Annuler le partage',
  'room.negotiating': 'Établissement de la connexion directe…',
  'room.negotiatingHint':
    'Recherche du chemin le plus court entre les deux appareils. Cette étape prend quelques secondes.',
  'room.reconnecting': 'Connexion interrompue — reprise en cours…',
  'room.reconnectingHint':
    'Ce qui a déjà été transféré est conservé. La reprise repart exactement là où elle s’est arrêtée.',
  'room.preparing': 'Préparation…',
  'room.sent': 'Envoi terminé',
  'room.received': 'Réception terminée',
  'room.receivedUnverified': 'Réception terminée, avec une réserve',
  'room.sentBody': 'Les fichiers ont été transmis et vérifiés par votre correspondant.',
  'room.receivedBody.one': 'Le fichier a été enregistré.',
  'room.receivedBody.other': '{count} fichiers ont été enregistrés.',
  'room.resultTotal': 'Total : {size}.',
  'room.verified': 'vérifié',
  'room.unverified': 'non vérifié',
  'room.finish': 'Terminer',
  'room.errorTitle': 'Le transfert n’a pas abouti',

  // ── Alertes ───────────────────────────────────────────────────────────────
  'alert.retry': 'Recommencer',

  // ── Erreurs serveur ───────────────────────────────────────────────────────
  'errors.roomFull': 'Ce partage est déjà en cours avec quelqu’un d’autre.',
  'errors.roleTaken': 'Ce lien est déjà ouvert par un appareil jouant le même rôle.',
  'errors.roomConsumed':
    'Ce lien a déjà été utilisé. Il ne fonctionne qu’une fois — demandez à la personne qui vous l’a envoyé de créer un nouveau partage.',
  'errors.rateLimited': 'Trop de tentatives en peu de temps. Patientez un instant puis rechargez.',
  'errors.unsupportedVersion':
    'Votre navigateur utilise une version du protocole incompatible. Rechargez la page.',
  'errors.relayDisabled':
    'La connexion directe est impossible et le relais est désactivé sur ce serveur.',
  'errors.payloadTooLarge': 'Message trop volumineux pour le serveur de mise en relation.',
  'errors.badMessage': 'Le serveur a rejeté un message mal formé.',
  'errors.internal': 'Erreur interne du serveur de mise en relation.',
  'errors.signaling':
    'Impossible de joindre le serveur pour établir la connexion. Vérifiez votre réseau, puis rechargez la page.',

  // ── Erreurs de transfert ──────────────────────────────────────────────────
  'errors.iceFailed':
    'Aucun chemin réseau n’a pu être établi avec l’autre appareil. Cela arrive sur certains réseaux d’entreprise ou mobiles qui bloquent les connexions directes.',
  'errors.connectionFailed':
    'La connexion directe a échoué. Les deux appareils sont probablement derrière des réseaux trop restrictifs (NAT symétrique, pare-feu d’entreprise). Essayez depuis un autre réseau, ou sur le même Wi-Fi.',
  'errors.dataChannel': 'Le canal de données a rencontré une erreur.',
  'errors.filesForgotten':
    'Les fichiers à envoyer ne sont plus disponibles (la page a été rechargée). Sélectionnez-les à nouveau.',
  'errors.acceptTimeout':
    'Le destinataire n’a pas répondu dans le délai imparti. Le lien a peut-être été ouvert sans être accepté.',
  'errors.rejected': 'Le destinataire a refusé le transfert.',
  'errors.peerLeft': 'Le correspondant s’est déconnecté pendant le transfert.',
  'errors.browserCannotReceive':
    'Ce fichier fait {size}, au-delà de ce que {browser} peut assembler en mémoire (limite fixée à {limit}). Ouvrez ce lien dans Chrome ou Edge pour recevoir les gros fichiers, ou demandez à votre correspondant de découper le fichier.',
  'errors.memoryWarning':
    'Sur {browser}, chaque fichier est assemblé en mémoire avant d’être enregistré. Gardez cet onglet au premier plan et évitez de le fermer.',
  'errors.destinationUnavailable':
    'Le navigateur a refusé l’accès au disque. Une politique de sécurité (ou une extension) bloque peut-être l’enregistrement de fichiers.',
  'errors.destinationDenied': 'L’autorisation d’écrire sur le disque a été refusée.',
  'errors.noPicker': 'Ce navigateur ne permet pas de choisir la destination du fichier reçu.',
  'errors.noDirectoryPicker': 'Ce navigateur ne permet pas de choisir un dossier de destination.',
  'errors.receiveImpossible': 'Réception impossible sur ce navigateur.',
  'errors.destinationUnavailableShort': 'Destination indisponible.',
  'errors.cancelledBySender': 'L’émetteur a annulé le transfert.',
  'errors.cancelled': 'Transfert annulé',
  'errors.rejectDeclined': 'Le destinataire a refusé le transfert.',
  'errors.rejectNoDestination':
    'Le destinataire n’a pas pu choisir où enregistrer le fichier. Il a peut-être fermé la fenêtre de sélection.',
  'errors.rejectCannotReceive':
    'Le navigateur du destinataire ne peut pas recevoir un fichier de cette taille.',
  'errors.integrityFailed':
    'Le transfert a été interrompu : les données reçues ne sont pas conformes. Relancez le partage.',
  'errors.incompleteReceive':
    '« {name} » n’a pas été reçu en entier : {received} sur {total} ({percent} %). Aucun fichier n’a été enregistré. Relancez le partage.',
  'errors.incompleteReceiveResume':
    '« {name} » n’a pas été reçu en entier : des morceaux manquent après la reprise. Aucun fichier n’a été enregistré. Relancez le partage.',
  'errors.checksumMismatch':
    '« {name} » : les données reçues ne correspondent pas à l’empreinte SHA-256 annoncée par l’émetteur. Le fichier n’a pas été enregistré — relancez le partage.',
  'errors.transferInterrupted':
    'Le transfert s’est interrompu de façon inattendue. Relancez le partage.',
  'errors.transferInterruptedAt':
    'Le transfert s’est interrompu à {percent} % ({received} sur {total}). Relancez le partage.',

  // ── Divers ────────────────────────────────────────────────────────────────
  'common.unknownError': 'Erreur inconnue',
} as const;

export type MessageKey = keyof typeof fr;

/**
 * Clés de base des pluriels. Chaque base doit posséder ses variantes `.one` et `.other` —
 * ce que le contrôle de type en fin de fichier vérifie.
 */
export type PluralBase =
  'home.filesReady' | 'home.andMore' | 'incoming.title' | 'incoming.andMore' | 'room.receivedBody';

export type PluralKey<B extends string> = `${B}.one` | `${B}.other`;

/**
 * L'anglais doit couvrir **exactement** les clés du français.
 * Une omission ou un ajout est signalé à la compilation.
 */
export const en: Record<MessageKey, string> = {
  'app.brand': 'Attrape!',
  'app.tagline': 'No file is ever stored on the server',
  'app.titleHome': 'Direct file sharing — no server, no size limit',
  'app.titleRoom': 'Transfer in progress…',
  'app.footerMain': 'Direct transfer, end-to-end encrypted. Close the tab, the share stops.',
  'app.footerDetail': 'Self-hosted instance — the key never reaches the server.',
  'app.language': 'Language',
  'app.themeToLight': 'Switch to light theme',
  'app.themeToDark': 'Switch to dark theme',

  'home.title': 'Send a file, with no size limit',
  'home.titleLine1': 'Share',
  'home.titleLine2': 'directly.',
  'home.tag': 'Simple · Private · No limit',
  'home.lede':
    'Files go directly from your device to your correspondent’s. They are never uploaded or kept: close the tab and the transfer stops.',
  'home.filesReady.one': '{count} file ready',
  'home.filesReady.other': '{count} files ready',
  'home.andMore.one': 'and {count} more…',
  'home.andMore.other': 'and {count} more…',
  'home.createLink': 'Create the share link',
  'home.allowMultiple': 'Allow multiple recipients',
  'home.allowMultipleOn':
    'The link stays valid: several people can open it for as long as the room exists.',
  'home.allowMultipleOff':
    'The link works only once. As soon as the person who opened it leaves, it becomes unusable — even for them.',
  'home.clearAll': 'Remove all',
  'home.explainLabel': 'How it works',
  'home.explain1Title': 'Drop files',
  'home.explain1Body': 'They stay on your device.',
  'home.explain2Title': 'Share the link',
  'home.explain2Body': 'The key travels with it, never to the server.',
  'home.explain3Title': 'Direct connection',
  'home.explain3Body': 'Your devices talk peer-to-peer.',
  'home.explain4Title': 'Secure transfer',
  'home.explain4Body': 'End-to-end encrypted, automatically.',
  'home.badgeLine1': 'No server.',
  'home.badgeLine2': '100% you.',
  'home.deviceSender': 'Your device',
  'home.deviceReceiver': 'Receiving device',
  'home.techLabel': 'Security & technology',
  'home.tech1Value': 'AES-256',
  'home.tech1Label': 'End-to-end encryption',
  'home.tech2Value': 'SHA-256',
  'home.tech2Label': 'Integrity verified on arrival',
  'home.tech3Value': 'WebRTC',
  'home.tech3Label': 'Direct P2P connection between browsers',
  'home.tech4Value': '0',
  'home.tech4Label': 'Files stored on the server',

  'drop.title': 'Choose files to send',
  'drop.hint': 'or drag them here — or paste them with Ctrl/⌘ + V',

  'share.copy': 'Copy the link',
  'share.copied': 'Copied',
  'share.label': 'Share link',
  'share.hint':
    'The key, after the # character, is never sent to the server. Do not post this link publicly.',
  'share.qrLabel': 'Code to scan and open this link',
  'share.qrHint': 'Scan this code with your phone’s camera.',
  'share.share': 'Share',
  'share.install': 'Install the app',

  'incoming.title.one': 'A correspondent wants to send you a file',
  'incoming.title.other': 'A correspondent wants to send you {count} files',
  'incoming.total': 'Total: {size}. Nothing will be written to disk before you agree.',
  'incoming.andMore.one': 'and {count} more…',
  'incoming.andMore.other': 'and {count} more…',
  'incoming.accept': 'Accept and choose the destination',
  'incoming.reject': 'Decline',

  'progress.preparing': 'Preparing the transfer…',
  'progress.fileOfTotal': 'File {index} of {count}',
  'progress.overall': '{percent}% of the whole',
  'progress.remaining': '{duration} left',
  'progress.cancel': 'Stop',
  'progress.sending': 'Sending',
  'progress.receiving': 'Receiving',

  'status.connected': 'Server connected',
  'status.connecting': 'Connecting to the server…',
  'status.reconnecting': 'Reconnecting…',
  'status.refused': 'Connection refused',
  'status.disconnected': 'Disconnected',
  'status.waiting': 'Waiting',
  'status.pathUnknown': 'Finding a route…',
  'status.directLocal': 'Direct connection — local network',
  'status.directPeer': 'Direct connection — peer to peer',
  'status.relayed': 'Connection relayed by the server',
  'room.relaySlow':
    'The direct connection between the two devices failed: data goes through the server relay, which is slower.',
  'room.relaySlowBig':
    'The direct connection between the two devices failed: data goes through the server relay, which is slower. For {size}, expect roughly {eta}. Keep both tabs open until then.',
  'status.established': 'Connection established',
  'status.roleSender': 'Sender',
  'status.roleReceiver': 'Receiver',

  'room.errorNoKey':
    'This link is incomplete: the decryption key, located after the # character, is missing. Ask your correspondent for the full link.',
  'room.createNewShare': 'Create a new share',
  'room.errorForgotten':
    'You created this share, but the files were forgotten when the page reloaded. The browser cannot recover them without your consent — that is also what guarantees nothing leaks.',
  'room.chooseFiles': 'Choose files',
  'room.newShare': 'New share',
  'room.connecting': 'Connecting to the server…',
  'room.connectingHint': 'Linking up with the service that puts the two devices in touch.',
  'room.waitingPeer': 'Waiting for the receiver',
  'room.waitingPeerHint':
    'Share this link. As soon as it is opened, the connection is established automatically.',
  'room.keepInMind': 'Keep in mind',
  'room.keep1': 'This tab must stay open: it is the one sending the files.',
  'room.keep2': 'The transfer starts as soon as your correspondent accepts.',
  'room.keep3':
    'If a direct connection is impossible, a message will tell you clearly instead of leaving you stuck.',
  'room.cancelShare': 'Cancel the share',
  'room.negotiating': 'Establishing the direct connection…',
  'room.negotiatingHint':
    'Looking for the shortest route between the two devices. This takes a few seconds.',
  'room.reconnecting': 'Connection lost — resuming…',
  'room.reconnectingHint':
    'What has already been transferred is kept. The transfer resumes exactly where it stopped.',
  'room.preparing': 'Preparing…',
  'room.sent': 'Sent',
  'room.received': 'Received',
  'room.receivedUnverified': 'Received, with one caveat',
  'room.sentBody': 'The files were transferred and verified by your correspondent.',
  'room.receivedBody.one': 'The file was saved.',
  'room.receivedBody.other': '{count} files were saved.',
  'room.resultTotal': 'Total: {size}.',
  'room.verified': 'verified',
  'room.unverified': 'not verified',
  'room.finish': 'Finish',
  'room.errorTitle': 'The transfer did not complete',

  'alert.retry': 'Try again',

  'errors.roomFull': 'This share is already in progress with someone else.',
  'errors.roleTaken': 'This link is already open on a device playing the same role.',
  'errors.roomConsumed':
    'This link has already been used. It only works once — ask the person who sent it to create a new share.',
  'errors.rateLimited': 'Too many attempts in a short time. Wait a moment and reload.',
  'errors.unsupportedVersion':
    'Your browser uses an incompatible protocol version. Reload the page.',
  'errors.relayDisabled':
    'A direct connection is impossible and relaying is disabled on this server.',
  'errors.payloadTooLarge': 'Message too large for the connection server.',
  'errors.badMessage': 'The server rejected a malformed message.',
  'errors.internal': 'Internal error in the connection server.',
  'errors.signaling':
    'Could not reach the server to establish the connection. Check your network, then reload the page.',

  'errors.iceFailed':
    'No network route could be established with the other device. This happens on some corporate or mobile networks that block direct connections.',
  'errors.connectionFailed':
    'The direct connection failed. Both devices are probably behind overly restrictive networks (symmetric NAT, corporate firewall). Try from another network, or on the same Wi-Fi.',
  'errors.dataChannel': 'The data channel hit an error.',
  'errors.filesForgotten':
    'The files to send are no longer available (the page was reloaded). Select them again.',
  'errors.acceptTimeout':
    'The receiver did not answer in time. The link may have been opened without being accepted.',
  'errors.rejected': 'The receiver declined the transfer.',
  'errors.peerLeft': 'Your correspondent disconnected during the transfer.',
  'errors.browserCannotReceive':
    'This file is {size}, beyond what {browser} can assemble in memory (limit set to {limit}). Open this link in Chrome or Edge to receive large files, or ask your correspondent to split the file.',
  'errors.memoryWarning':
    'On {browser}, each file is assembled in memory before being saved. Keep this tab in the foreground and avoid closing it.',
  'errors.destinationUnavailable':
    'The browser refused access to the disk. A security policy (or an extension) may be blocking file saving.',
  'errors.destinationDenied': 'Permission to write to disk was denied.',
  'errors.noPicker': 'This browser cannot let you choose where the received file goes.',
  'errors.noDirectoryPicker': 'This browser cannot let you choose a destination folder.',
  'errors.receiveImpossible': 'Receiving is not possible in this browser.',
  'errors.destinationUnavailableShort': 'Destination unavailable.',
  'errors.cancelledBySender': 'The sender cancelled the transfer.',
  'errors.cancelled': 'Transfer cancelled',
  'errors.rejectDeclined': 'The receiver declined the transfer.',
  'errors.rejectNoDestination':
    'The receiver could not choose where to save the file. They may have closed the save dialog.',
  'errors.rejectCannotReceive': 'The receiver’s browser cannot handle a file of this size.',
  'errors.integrityFailed':
    'The transfer was interrupted: the data received does not match. Start the share again.',
  'errors.incompleteReceive':
    '“{name}” did not arrive complete: {received} of {total} ({percent} %). Nothing was saved. Start the share again.',
  'errors.incompleteReceiveResume':
    '“{name}” did not arrive complete: parts are missing after resuming. Nothing was saved. Start the share again.',
  'errors.checksumMismatch':
    '“{name}”: the data received does not match the SHA-256 fingerprint announced by the sender. The file was not saved — start the share again.',
  'errors.transferInterrupted': 'The transfer stopped unexpectedly. Start the share again.',
  'errors.transferInterruptedAt':
    'The transfer stopped at {percent} % ({received} of {total}). Start the share again.',

  'common.unknownError': 'Unknown error',
};

/**
 * Contrôle à la compilation : chaque base de pluriel doit avoir ses deux variantes.
 *
 * Sans cette assertion, une base déclarée sans son `.other` compilerait sans broncher et
 * produirait une clé technique affichée à l'utilisateur au moment précis où il y a le plus
 * d'un fichier.
 */
type PluralsComplete = PluralKey<PluralBase> extends MessageKey ? true : never;
const pluralsAreComplete: PluralsComplete = true;
void pluralsAreComplete;

export const catalogs: Record<Locale, Record<MessageKey, string>> = { fr, en };
